import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './QuoteDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[code] || code || '$';
};

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [quote, setQuote] = useState(null);
  const [rfq, setRfq] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const quoteRef = useRef(null);

  const fetchQuoteDetails = useCallback(async () => {
    if (!token || !id) return;

    try {
      setLoading(true);

      const bidRes = await fetch(`${API_BASE}/bids/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bidData = await bidRes.json();

      if (!bidData.success) throw new Error(bidData.message);

      setQuote(bidData.bid);

      if (bidData.bid.vendorId) {
        const vendorRes = await fetch(`${API_BASE}/users/${bidData.bid.vendorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const vendorData = await vendorRes.json();
        if (vendorData.success) setVendorDetails(vendorData.user);
      }

      if (bidData.bid.rfqId) {
        const rfqRes = await fetch(`${API_BASE}/rfqs/${bidData.bid.rfqId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rfqData = await rfqRes.json();
        if (rfqData.success) {
          setRfq(rfqData.rfq);

          if (rfqData.rfq.ownerId) {
            const ownerRes = await fetch(`${API_BASE}/users/${rfqData.rfq.ownerId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const ownerData = await ownerRes.json();
            if (ownerData.success) setOwnerDetails(ownerData.user);
          }
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchQuoteDetails();
  }, [fetchQuoteDetails]);

  const handleDownloadPDF = async () => {
    if (!quoteRef.current || downloading) return;

    setDownloading(true);

    try {
      const canvas = await html2canvas(quoteRef.current, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png', 1.0);

      let position = 0;
      let heightLeft = imgHeight;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Quotation_${quote?._id?.slice(-8)}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('PDF error:', e);
      alert('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  const calculateSubtotal = () => {
    if (!quote?.quotationItems) return 0;
    return quote.quotationItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const subtotal = calculateSubtotal();
  const total = quote?.amount || subtotal;
  const currencyCode = quote?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currencyCode);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getValidTill = () => {
    if (!quote?.createdAt) return 'N/A';
    const d = new Date(quote.createdAt);
    d.setDate(d.getDate() + (quote.validityDays || 30));
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getQuoteNumber = () => {
    if (!quote?._id || !quote?.createdAt) return 'SZ-0001';
    const year = new Date(quote.createdAt).getFullYear();
    const timestampHex = quote._id.toString().substring(0, 8);
    const timestampDecimal = parseInt(timestampHex, 16);
    const serial = timestampDecimal.toString().slice(-4).padStart(4, '0');
    return `SZ-${year}-${serial}`;
  };

  const quoteNumber = getQuoteNumber();

  if (loading) {
    return (
      <div className="quote-loading">
        <div className="quote-spinner"></div>
        <p>Loading quotation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quote-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="quote-back-btn">Go Back</button>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="quote-error">
        <p>Quotation not found</p>
        <button onClick={() => navigate('/service-provider/my-offers')} className="quote-back-btn">
          Back to My Quotations
        </button>
      </div>
    );
  }

  return (
    <div className="quote-detail-container">
      <div className="quote-print-actions no-print">
        <button onClick={handleDownloadPDF} className="quote-pdf-btn" disabled={downloading}>
          {downloading ? (
            <><i className="fas fa-spinner fa-spin"></i> Generating...</>
          ) : (
            <><i className="fas fa-file-pdf"></i> Download PDF</>
          )}
        </button>
      </div>

      <div className="quote-paper" ref={quoteRef}>
        <div className="quote-header-invoice">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/seazex_new_logo.png"
              alt="SeaZex"
              style={{ width: '36px', height: '36px', marginRight: '12px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div className="quote-logo-invoice" style={{ display: 'flex' }}>
                <span className="logo-sea-white">Sea</span>
                <span className="logo-zex-green">Zex</span>
              </div>
              <div className="logo-tagline">Maritime Marketplace</div>
            </div>
          </div>
          <div className="quote-title-invoice">
            <div className="quote-badge">QUOTATION</div>
            <div className="quote-number-invoice"># {quoteNumber}</div>
          </div>
        </div>

        <div className="invoice-info-grid">
          <div className="info-block">
            <div className="block-title">FROM</div>
            <div className="block-content">
              <div className="info-row">
                <span className="info-label">Company</span>
                <span className="info-value">
                  {vendorDetails?.company || vendorDetails?.name || quote.vendorName}
                </span>
              </div>
              {vendorDetails?.registrationNo && (
                <div className="info-row">
                  <span className="info-label">Reg. No.</span>
                  <span className="info-value">{vendorDetails.registrationNo}</span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Service</span>
                <span className="info-value">{rfq?.serviceType || 'Maritime'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Date</span>
                <span className="info-value">{formatDate(quote.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Valid Until</span>
                <span className="info-value">{getValidTill()}</span>
              </div>
            </div>
          </div>

          <div className="info-block">
            <div className="block-title">TO</div>
            <div className="block-content">
              <div className="info-row">
                <span className="info-label">Vessel</span>
                <span className="info-value">{rfq?.vesselName || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Service</span>
                <span className="info-value">{rfq?.serviceType || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Port</span>
                <span className="info-value">{rfq?.port || 'N/A'}</span>
              </div>
              {rfq?.nextPort && (
                <div className="info-row">
                  <span className="info-label">Next Port</span>
                  <span className="info-value">{rfq.nextPort}</span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Client</span>
                <span className="info-value">
                  {ownerDetails?.name || rfq?.ownerName || 'N/A'}
                </span>
              </div>
              {ownerDetails?.company && (
                <div className="info-row">
                  <span className="info-label">Company</span>
                  <span className="info-value">{ownerDetails.company}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="invoice-table-wrapper">
          <table className="invoice-items-table">
            <thead>
              <tr>
                <th className="col-sno">#</th>
                <th className="col-desc">Item Description</th>
                <th className="col-qty">Qty</th>
                <th className="col-price">Unit Price</th>
                <th className="col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.quotationItems && quote.quotationItems.length > 0 ? (
                <>
                  {quote.quotationItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="col-sno">{idx + 1}</td>
                      <td className="col-desc">{item.description}</td>
                      <td className="col-qty">{item.quantity}</td>
                      <td className="col-price">
                        {currencySymbol} {item.unitPrice.toLocaleString()} {currencyCode}
                      </td>
                      <td className="col-total">
                        {currencySymbol} {(item.quantity * item.unitPrice).toLocaleString()} {currencyCode}
                      </td>
                    </tr>
                  ))}
                  {quote.quotationItems.length < 8 &&
                    Array.from({ length: 8 - quote.quotationItems.length }).map((_, i) => (
                      <tr key={`empty-${i}`}>
                        <td className="col-sno">&nbsp;</td>
                        <td className="col-desc">&nbsp;</td>
                        <td className="col-qty">&nbsp;</td>
                        <td className="col-price">&nbsp;</td>
                        <td className="col-total">&nbsp;</td>
                      </tr>
                    ))}
                </>
              ) : (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td className="col-sno">&nbsp;</td>
                    <td className="col-desc">&nbsp;</td>
                    <td className="col-qty">&nbsp;</td>
                    <td className="col-price">&nbsp;</td>
                    <td className="col-total">&nbsp;</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="invoice-footer-section">
          <div className="signature-block">
            <div className="signature-label">Authorized Signatory</div>
            <div
              className="signature-name"
              style={{
                fontFamily: "'Jonathan Signature', 'Brush Script MT', 'Segoe Script', cursive",
                fontSize: '32px'
              }}
            >
              {quote.signatureName || '_________________'}
            </div>
            <div className="signature-title">
              For and behalf of {vendorDetails?.company || quote.vendorName}
            </div>
          </div>

          <div className="totals-block">
            <div className="total-row">
              <span>Subtotal</span>
              <span>{currencySymbol} {subtotal.toLocaleString()} {currencyCode}</span>
            </div>
            <div className="total-row grand-total">
              <span>Total Amount</span>
              <span>{currencySymbol} {total.toLocaleString()} {currencyCode}</span>
            </div>
          </div>
        </div>

        <div className="invoice-terms">
          <div className="terms-heading">TERMS & CONDITIONS</div>
          <div className="terms-text">
            {quote.termsConditions ||
              `This quotation is valid for ${quote.validityDays || 30} days from the date of issue. All prices are exclusive of applicable taxes.`}
          </div>
        </div>

        <div className="invoice-footer">
          <div className="footer-text">SeaZex - The Secure Maritime Service Marketplace</div>
          <div className="footer-text-small">www.seazex.com | support@seazex.com</div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetail;